class BundlePicker {
  constructor(el) {
    this.el = el;
    this.mode = 'bundle';
    this.optionCards = el.querySelectorAll('[data-option]');
    this.singlePanel = el.querySelector('[data-panel="single"]');
    this.bundlePanel = el.querySelector('[data-panel="bundle"]');
    this.sizePills = el.querySelectorAll('[data-size-pill]');
    this.selectedSizeInput = el.querySelector('[data-selected-size]');
    this.sizeLabel = el.querySelector('[data-size-label-value]');
    this.cards = el.querySelectorAll('[data-card]');
    this.maxSelect = 3;
    this.selected = new Set();
    this.cta = el.querySelector('[data-bundle-cta]');
    this.error = el.querySelector('[data-bundle-error]');

    this.cards.forEach((card) => {
      if (card.classList.contains('bundle-picker__card--current')) {
        this.selected.add(card.dataset.productHandle);
        this.fetchSizes(card);
      }
    });

    this.setMode('single');
    this.bindEvents();
  }

  bindEvents() {
    this.optionCards.forEach((card) => {
      card.addEventListener('click', () => this.setMode(card.dataset.option));
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.setMode(card.dataset.option);
        }
      });
    });

    this.sizePills.forEach((pill) => {
      pill.addEventListener('click', () => {
        this.sizePills.forEach((p) => p.classList.remove('is-selected'));
        pill.classList.add('is-selected');
        if (this.sizeLabel) this.sizeLabel.textContent = pill.dataset.sizeTitle;
        if (this.selectedSizeInput) this.selectedSizeInput.value = pill.dataset.variantId;
        this.updateCTA();
      });
    });

    this.cards.forEach((card) => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('[data-size-select]')) return;
        this.toggleCard(card);
      });
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggleCard(card);
        }
      });
      card.setAttribute('tabindex', '0');
    });

    this.el.addEventListener('change', (e) => {
      if (e.target.matches('[data-size-select]')) this.updateCTA();
    });

    if (this.cta) {
      this.cta.addEventListener('click', () => {
        if (this.mode === 'single') this.addSingleToCart();
        else this.addBundleToCart();
      });
    }
  }

  setMode(mode) {
    this.mode = mode;
    this.optionCards.forEach((card) => {
      card.classList.toggle('is-active', card.dataset.option === mode);
      card.setAttribute('aria-selected', card.dataset.option === mode ? 'true' : 'false');
    });

    const singleExpand = this.el.querySelector('[data-expandable="single"]');
    const bundleExpand = this.el.querySelector('[data-expandable="bundle"]');
    if (singleExpand) {
      singleExpand.classList.toggle('is-open', mode === 'single');
      singleExpand.hidden = mode !== 'single';
    }
    if (bundleExpand) {
      bundleExpand.classList.toggle('is-open', mode === 'bundle');
      bundleExpand.hidden = mode !== 'bundle';
    }
    this.updateCTA();
  }

  toggleCard(card) {
    const handle = card.dataset.productHandle;
    const isSelected = this.selected.has(handle);
    const currentHandle = this.el.querySelector('.bundle-picker__card--current')?.dataset.productHandle;

    if (isSelected) {
      if (handle === currentHandle) return;
      this.selected.delete(handle);
      card.dataset.selected = 'false';
      card.setAttribute('aria-checked', 'false');
    } else {
      if (this.selected.size >= this.maxSelect) {
        for (const h of this.selected) {
          if (h !== currentHandle) {
            this.selected.delete(h);
            const old = this.el.querySelector(`[data-product-handle="${h}"]`);
            if (old) old.dataset.selected = 'false';
            if (old) old.setAttribute('aria-checked', 'false');
            break;
          }
        }
      }
      this.selected.add(handle);
      card.dataset.selected = 'true';
      card.setAttribute('aria-checked', 'true');
      if (!card.dataset.sizesLoaded) this.fetchSizes(card);
    }
    this.updateCTA();
  }

  async fetchSizes(card) {
    if (card.dataset.sizesLoaded) return;
    const select = card.querySelector('[data-size-select]');
    if (!select) return;
    try {
      const res = await fetch(`/products/${card.dataset.productHandle}.js`);
      const data = await res.json();
      select.innerHTML = data.variants
        .map((v) => `<option value="${v.id}"${v.available ? '' : ' disabled'}>${v.title}${v.available ? '' : ' (Sold out)'}</option>`)
        .join('');
      select.disabled = false;
      card.dataset.sizesLoaded = 'true';
      this.updateCTA();
    } catch (error) {
      select.innerHTML = '<option disabled>Error loading</option>';
    }
  }

  updateCTA() {
    if (!this.cta) return;

    if (this.mode === 'single') {
      const hasSize = this.selectedSizeInput?.value || this.el.querySelector('.bundle-picker__size-pill.is-selected');
      this.cta.disabled = !hasSize;
      this.cta.textContent = 'Add to cart';
    } else {
      const allSized = [...this.selected].every((h) => {
        const card = this.el.querySelector(`[data-product-handle="${h}"]`);
        const select = card?.querySelector('[data-size-select]');
        return select && select.value && select.value !== '' && !select.disabled;
      });
      const bundlePrice = this.el.dataset.bundlePrice || '4499';
      this.cta.disabled = this.selected.size !== this.maxSelect || !allSized;
      this.cta.textContent = `Add 3 to Cart — ₹${bundlePrice}`;
    }
  }

  getCartSectionId() {
    const cartDrawer = document.querySelector('#CartDrawer');
    const cartPage = document.querySelector('cart-page');
    if (cartDrawer) return cartDrawer.dataset.sectionId;
    if (cartPage) return cartPage.dataset.sectionId;
    return 'cart-notification-popup-content';
  }

  parseHTML(markup) {
    return new DOMParser().parseFromString(markup, 'text/html');
  }

  async addItemsToCart(items) {
    const sectionId = this.getCartSectionId();
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items,
        sections: sectionId,
        sections_url: window.location.pathname
      })
    });
    if (!res.ok) throw new Error('Cart error');
    return res.json();
  }

  async openCartLikeTheme(parsedState) {
    const cartDrawer = document.querySelector('#CartDrawer');
    const cartPopup = document.querySelector('#CartNotificationPopup');
    const cartPage = document.querySelector('cart-page');
    const sectionId = this.getCartSectionId();
    const sectionHTML = parsedState?.sections?.[sectionId];
    const parsedNode = sectionHTML ? this.parseHTML(sectionHTML) : null;

    if (cartPage && parsedNode) {
      cartPage.updateEmptyStatus(false);
      if (typeof cartPage.purchaseHandler === 'function') {
        cartPage.purchaseHandler(parsedNode, parsedState);
      }
      return;
    }

    if (cartDrawer && parsedNode) {
      if (typeof cartDrawer.updateAsideProductsByParsedState === 'function') {
        cartDrawer.updateAsideProductsByParsedState(parsedState);
      }
      if (typeof cartDrawer.updateEmptyStatus === 'function') {
        cartDrawer.updateEmptyStatus();
      }
      if (typeof cartDrawer.purchaseHandler === 'function') {
        await cartDrawer.purchaseHandler(parsedNode, parsedState);
      }
      return;
    }

    if (cartPopup && parsedNode && typeof cartPopup.purchaseHandler === 'function') {
      cartPopup.purchaseHandler(parsedNode, parsedState);
      return;
    }

    this.triggerCartOpen();
  }

  async addSingleToCart() {
    const pill = this.el.querySelector('.bundle-picker__size-pill.is-selected');
    const variantId = pill?.dataset.variantId || this.selectedSizeInput?.value;
    if (!variantId) return;
    this.cta.disabled = true;
    try {
      const parsedState = await this.addItemsToCart([{ id: parseInt(variantId, 10), quantity: 1 }]);
      await this.openCartLikeTheme(parsedState);
    } catch (error) {
      this.showError('Something went wrong. Please try again.');
    } finally {
      this.cta.disabled = false;
      this.updateCTA();
    }
  }

  async addBundleToCart() {
    this.cta.disabled = true;
    this.error.hidden = true;
    const items = [...this.selected].map((h) => {
      const card = this.el.querySelector(`[data-product-handle="${h}"]`);
      const select = card.querySelector('[data-size-select]');
      return { id: parseInt(select.value, 10), quantity: 1 };
    });
    try {
      const parsedState = await this.addItemsToCart(items);
      await this.openCartLikeTheme(parsedState);
    } catch (error) {
      this.showError('Something went wrong. Please try again.');
    } finally {
      this.cta.disabled = false;
      this.updateCTA();
    }
  }

  triggerCartOpen() {
    const events = ['cart:open', 'cart:refresh', 'theme:cart:open'];
    let opened = false;
    for (const evt of events) {
      try {
        document.dispatchEvent(new CustomEvent(evt));
        opened = true;
        break;
      } catch (error) {}
    }
    if (!opened) window.location.href = '/cart';
  }

  showError(msg) {
    this.error.textContent = msg;
    this.error.hidden = false;
  }

}

document.querySelectorAll('[data-bundle-picker]').forEach((el) => new BundlePicker(el));
